import type React from "react";
import { createContext, useContext, useMemo } from "react";
import type { UiTextDictionaryId } from "src/gql/__generated__/types";
import type { UITextDictionary, UITextKey } from "./dictionaries";
import { getUITextDictionary } from "./dictionaries";

export type UITextTranslator = (key: UITextKey) => string;

type UITextContextValue = {
  dictionary: UITextDictionary;
  t: UITextTranslator;
};

const UITextContext = createContext<UITextContextValue | null>(null);

type UITextProviderProps = {
  children: React.ReactNode;
  uiTextDictionaryId?: UiTextDictionaryId;
};

export const UITextProvider: React.FC<UITextProviderProps> = ({
  children,
  uiTextDictionaryId = "default",
}) => {
  const dictionary = useMemo(() => {
    return getUITextDictionary(uiTextDictionaryId);
  }, [uiTextDictionaryId]);

  const t = useMemo(
    () => (key: UITextKey) => {
      return dictionary.texts[key];
    },
    [dictionary.texts],
  );

  const value = useMemo(() => ({ dictionary, t }), [dictionary, t]);

  return (
    <UITextContext.Provider value={value}>{children}</UITextContext.Provider>
  );
};

export const useUIText = () => {
  const context = useContext(UITextContext);
  if (!context) {
    throw new Error("useUIText must be used within UITextProvider");
  }
  return context;
};
